import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getApps } from "../services/api";
import { 
  ArrowRight, 
  Cpu, 
  ShieldCheck, 
  Globe, 
  Zap, 
  Layers, 
  TrendingUp, 
  ChevronRight, 
  Activity,
  Mic,
  PhoneCall,
  Star,
  Sparkles,
  ShoppingCart,
  Network,
  Gamepad2,
  Bot,
  GraduationCap,
  ArrowUpRight
} from "lucide-react";
import Logo from "../assets/quivix.png";

const getAppImage = (app) =>
  app?.image || app?.icon_url || app?.cover_image || Logo;

const reviews = [
  {
    id: 1,
    name: "Chafadi Commerce",
    role: "E-Commerce Platform",
    company: "Chafadi Platform",
    rating: 5,
    comment: "QuivixDigital architected our high-throughput e-commerce platform infrastructure, handling high-volume order processing with zero downtime.",
    icon: <ShoppingCart className="w-4 h-4 text-emerald-400" />,
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=256&q=80",
    initials: "CP"
  },
  {
    id: 2,
    name: "Nzomi Inc.",
    role: "Networking & Cloud Systems",
    company: "Nzomi Inc.",
    rating: 5,
    comment: "We partnered with QuivixDigital for our core networking architecture. Their low-latency node engineering reduced our server response times significantly.",
    icon: <Network className="w-4 h-4 text-blue-400" />,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=256&q=80",
    initials: "NI"
  },
  {
    id: 3,
    name: "MTG Infomatic",
    role: "Apps, Games & Utility Suite",
    company: "MTG Infomatic",
    rating: 5,
    comment: "QuivixDigital provided high-performance mobile apps, gaming engines, and utility tools that serve over 100k active users flawlessly.",
    icon: <Gamepad2 className="w-4 h-4 text-purple-400" />,
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=256&q=80",
    initials: "MI"
  },
  {
    id: 4,
    name: "Agentic AI Labs",
    role: "Autonomous AI Systems",
    company: "Agentic AI Corp",
    rating: 5,
    comment: "We built our autonomous agentic AI pipelines with QuivixDigital. They served our extreme neural compute requirements flawlessly.",
    icon: <Bot className="w-4 h-4 text-amber-400" />,
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=256&q=80",
    initials: "AI"
  }
];

const HomePage = () => {
  const [apps, setApps] = useState([]);

  useEffect(() => {
    const fetchFeaturedApps = async () => {
      try {
        const response = await getApps();
        const data = Array.isArray(response) ? response : response?.data || [];
        setApps(data.slice(0, 3));
      } catch (error) {
        console.error("Failed to load apps:", error);
      }
    };
    fetchFeaturedApps();
  }, []);

  const metrics = [
    { label: "Active Users", value: "60K+", icon: <Globe className="w-4 h-4 text-blue-400" />, change: "60k - 100k Users" },
    { label: "Platform Uptime", value: "99.99%", icon: <Activity className="w-4 h-4 text-emerald-400" />, change: "SLA Guaranteed" },
    { label: "Network Latency", value: "20 - 30ms", icon: <Zap className="w-4 h-4 text-amber-400" />, change: "~25ms Average" },
    { label: "Dedicated Servers", value: "18", icon: <Cpu className="w-4 h-4 text-indigo-400" />, change: "Global Cluster" }
  ];

  const pillars = [
    {
      icon: <Cpu className="w-6 h-6 text-blue-400" />,
      title: "Neural Engine & AI",
      description: "Proprietary high-throughput pipelines built for real-time data processing and autonomous decisions."
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-emerald-400" />,
      title: "Zero-Trust Security",
      description: "End-to-end cryptographic verifiability, data integrity, and privacy by design."
    },
    {
      icon: <Zap className="w-6 h-6 text-amber-400" />,
      title: "Low Latency Compute",
      description: "Micro-kernels optimized for 20-30ms response times across 18 global dedicated servers."
    },
    {
      icon: <Layers className="w-6 h-6 text-indigo-400" />,
      title: "QuivixDigital Ecosystem",
      description: "Single unified identity and developer platform powering our suite of applications."
    }
  ];

  return (
    <div className="bg-black text-[#f5f5f7] min-h-screen font-sans selection:bg-blue-500/30">
      
      {/* ================= HERO SECTION ================= */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden bg-radial-ambient">
        {/* Subtle background ambient mesh */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] sm:w-[900px] h-[400px] bg-gradient-to-tr from-blue-600/10 via-indigo-600/10 to-transparent blur-[140px] pointer-events-none rounded-full" />
        
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          
          {/* Release Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] backdrop-blur-md mb-6 hover:border-white/20 transition-all duration-300">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-xs font-mono text-[#86868b] tracking-tight">
              QuivixDigital Core v3.0 Live
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-[#86868b]" />
          </div>

          {/* Master Headline */}
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight text-white leading-[1.08] max-w-4xl mx-auto font-heading">
            Engineering High-Performance <span className="text-gradient-blue">Digital Products</span>.
          </h1>

          {/* Subheadline */}
          <p className="mt-6 text-base sm:text-xl text-[#86868b] max-w-2xl mx-auto font-normal leading-relaxed">
            QuivixDigital builds modern consumer platforms, real-time telemetry systems, and intelligent digital solutions for over 60k+ active users.
          </p>

          {/* Action CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap">
            <Link
              to="/apps"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-neutral-200 transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.25)] active:scale-95"
            >
              <span>Explore Products</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://careers.quivixdigital.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/10 text-xs font-semibold transition-all duration-300 active:scale-95"
            >
              <GraduationCap className="w-4 h-4 text-sky-400" />
              <span>Careers & Internships</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />
            </a>
            <Link
              to="/invest"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-white/[0.05] text-white border border-white/10 hover:bg-white/10 hover:border-white/20 backdrop-blur-md font-medium text-sm transition-all duration-300 active:scale-95"
            >
              <span>Investor Portal</span>
              <TrendingUp className="w-4 h-4 text-blue-400" />
            </Link>
          </div>

          {/* Metrics Row */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
            {metrics.map((m, idx) => (
              <div
                key={idx}
                className="apple-glass rounded-2xl p-5 text-left apple-card-hover flex flex-col justify-between"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-mono uppercase tracking-wider text-[#86868b] flex items-center gap-1.5">
                    {m.icon}
                    {m.label}
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-2xl sm:text-3xl font-bold text-white tracking-tight font-heading">
                    {m.value}
                  </div>
                  <div className="text-[11px] text-emerald-400 mt-1 font-mono">
                    {m.change}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ================= VOICE AUTOMATION SECTION ================= */}
      <section className="py-24 border-t border-white/[0.08] bg-black relative overflow-hidden">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[520px] h-[260px] bg-blue-600/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 sm:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
            <div className="max-w-3xl">
              <span className="text-xs font-mono uppercase tracking-widest text-sky-400">Voice Automation</span>
              <h2 className="text-3xl sm:text-5xl font-bold tracking-tight text-white mt-2 font-heading">
                Conversational bots for serious call-center volume.
              </h2>
              <p className="text-sm text-[#86868b] mt-4 leading-relaxed max-w-2xl">
                QuivixDigital provides configurable English voice bots that listen, respond, qualify, and route calls through your existing VICIdial and Asterisk workflow.
              </p>
            </div>

            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-black font-semibold text-xs hover:bg-slate-200 transition-all shrink-0 active:scale-95"
            >
              <span>Discuss a Deployment</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Bot Network visual */}
          <div className="relative rounded-3xl border border-white/[0.1] bg-[#080a10] p-4 sm:p-6 mb-7 overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.42)]">
            <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-indigo-500/[0.08] via-blue-500/[0.03] to-transparent pointer-events-none" />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-indigo-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-300 animate-pulse" />
                  Quivix bot network
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mt-1 font-heading">A voice for every workflow.</h3>
              </div>
              <div className="inline-flex items-center gap-2 self-start md:self-auto px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.1] text-[10px] font-mono text-[#b5bac7]">
                <Activity className="w-3 h-3 text-emerald-300" />
                Shared intelligence layer
              </div>
            </div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-5">
              <div className="lg:col-span-5 rounded-2xl border border-white/[0.09] bg-gradient-to-br from-[#161c2d] to-[#090b12] p-5 overflow-hidden relative">
                <div className="absolute -right-10 -bottom-16 w-44 h-44 rounded-full bg-blue-400/10 blur-3xl pointer-events-none" />
                <div className="flex items-center gap-4 relative z-10">
                  <div className="relative w-16 h-16 shrink-0 flex items-center justify-center">
                    <div className="absolute inset-0 rounded-full border border-blue-300/25" />
                    <div className="absolute inset-2 rounded-full border border-indigo-300/30 border-dashed animate-spin [animation-duration:8s]" />
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-300 via-blue-500 to-indigo-700 flex items-center justify-center shadow-[0_0_30px_rgba(96,165,250,0.38)]">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#86868b]">Active personality</div>
                    <div className="text-xl font-bold text-white mt-1 font-heading">Emily Carter</div>
                    <div className="text-[10px] text-sky-300 mt-1 font-mono">Warm voice / English US</div>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-3 gap-2">
                  {[["12", "calls"], ["280ms", "response"], ["99.2%", "uptime"]].map(([value, label]) => (
                    <div key={label} className="rounded-xl border border-white/[0.08] bg-black/20 px-2.5 py-2">
                      <div className="text-sm font-bold text-white font-heading">{value}</div>
                      <div className="text-[9px] font-mono uppercase tracking-wide text-[#686e81] mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-2 text-[10px] font-mono text-emerald-300">
                  <Mic className="w-3 h-3" /> Live listening
                  <span className="ml-auto text-[#686e81]">voice activity detected</span>
                </div>
              </div>

              <div className="lg:col-span-7 rounded-2xl border border-white/[0.09] bg-white/[0.02] p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#86868b]">Agent constellation</div>
                    <div className="text-sm font-semibold text-white mt-1">Specialized voices, one reliable core</div>
                  </div>
                  <Sparkles className="w-4 h-4 text-amber-300" />
                </div>
                <div className="space-y-2.5">
                  {[
                    { name: "Daniel Brooks", role: "Steady / qualification", orb: "from-indigo-400 to-violet-700", load: "76%" },
                    { name: "Hannah Reed", role: "Bright / follow-up", orb: "from-fuchsia-400 to-pink-700", load: "64%" },
                    { name: "Custom profile", role: "Your script / your voice", orb: "from-emerald-300 to-teal-700", load: "ready" }
                  ].map((bot) => (
                    <div key={bot.name} className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-black/20 px-3 py-2.5">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${bot.orb} flex items-center justify-center shrink-0`}>
                        <Bot className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-semibold text-white truncate">{bot.name}</span>
                          <span className="text-[9px] font-mono text-emerald-300">{bot.load}</span>
                        </div>
                        <div className="text-[9px] text-[#686e81] mt-0.5">{bot.role}</div>
                      </div>
                      <div className="w-14 h-1 rounded-full bg-white/[0.08] overflow-hidden shrink-0">
                        <span className="block h-full rounded-full bg-gradient-to-r from-blue-400 to-indigo-400" style={{ width: bot.load === "ready" ? "28%" : bot.load }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-white/[0.08] flex flex-wrap items-center gap-x-4 gap-y-2 text-[9px] font-mono uppercase tracking-wider text-[#686e81]">
                  <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Ready</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Shared models</span>
                  <span className="inline-flex items-center gap-1.5"><PhoneCall className="w-3 h-3 text-sky-300" /> Routed by intent</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  icon: <Bot className="w-5 h-5 text-blue-400" />,
                  title: "Natural voice conversations",
                  description: "Live listening, responsive answers, approved pitch variations, and interruption-aware turn taking."
                },
                {
                  icon: <Cpu className="w-5 h-5 text-indigo-400" />,
                  title: "Conversation intelligence",
                  description: "Whisper speech recognition and a lightweight reasoning layer interpret imperfect phrases and caller intent."
                },
                {
                  icon: <ShieldCheck className="w-5 h-5 text-emerald-400" />,
                  title: "Controlled outcomes",
                  description: "Deterministic rules handle DNC, busy, no-answer, callback, eligibility, dead air, and transfer decisions."
                },
                {
                  icon: <Network className="w-5 h-5 text-sky-400" />,
                  title: "Built to integrate",
                  description: "AudioSocket connectivity, specialist transfer support, dispositions, logs, and deployment on QuivixDigital infrastructure."
                }
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.06 }}
                  className="apple-glass rounded-2xl p-5 border border-white/[0.08] apple-card-hover"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-sm font-bold text-white font-heading">{feature.title}</h3>
                  <p className="text-xs text-[#86868b] mt-2 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="lg:col-span-2 relative apple-glass rounded-3xl p-7 border border-blue-400/20 overflow-hidden bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-transparent">
              <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-blue-400/15 blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/20 text-[10px] font-mono uppercase tracking-widest text-blue-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                  Bot teams, configured your way
                </span>
                <h3 className="text-2xl font-bold text-white mt-5 font-heading">One platform. Multiple voices.</h3>
                <p className="text-xs text-[#a1a1aa] mt-3 leading-relaxed">
                  Give every bot its own name, voice, script, and routing profile while sharing the same reliable speech and decision infrastructure.
                </p>
                <div className="mt-6 pt-5 border-t border-white/[0.08] space-y-3">
                  {["VICIdial + Asterisk ready", "Local speech and intent processing", "Continuous improvement from reviewed conversations"].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-xs text-white/80">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-7 pt-5 border-t border-white/[0.08]">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#86868b]">Sample bot roster</span>
                    <span className="text-[10px] font-mono text-emerald-400">30+ identities</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { name: "Emily Carter", style: "Warm", orb: "from-sky-400 to-blue-600" },
                      { name: "Daniel Brooks", style: "Steady", orb: "from-indigo-400 to-violet-600" },
                      { name: "Hannah Reed", style: "Bright", orb: "from-fuchsia-400 to-pink-600" }
                    ].map((bot) => (
                      <div key={bot.name} className="rounded-xl border border-white/[0.08] bg-black/20 p-2.5 min-w-0">
                        <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${bot.orb} flex items-center justify-center shadow-[0_0_20px_rgba(96,165,250,0.22)]`}>
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="mt-2 truncate text-[10px] font-semibold text-white">{bot.name}</div>
                        <div className="mt-0.5 flex items-center gap-1 text-[9px] font-mono text-[#86868b]">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          {bot.style} voice
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= PILLARS OF EXCELLENCE ================= */}
      <section className="py-24 border-t border-white/[0.08] bg-[#050507]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="text-xs font-mono uppercase tracking-widest text-indigo-400">Engineering Philosophy</span>
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight text-white mt-2 font-heading">
              Built for High Performance.
            </h2>
            <p className="text-sm text-[#86868b] mt-4 leading-relaxed">
              Every product built by QuivixDigital is designed with low-latency response times (20-30ms), minimal software overhead, and extreme reliability across 18 dedicated servers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, idx) => (
              <div key={idx} className="apple-glass rounded-2xl p-6 border border-white/[0.08] apple-card-hover">
                <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-5">
                  {p.icon}
                </div>
                <h3 className="text-base font-bold text-white font-heading">{p.title}</h3>
                <p className="text-xs text-[#86868b] mt-2 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ================= ECOSYSTEM / PRODUCTS SECTION ================= */}
      <section className="py-24 border-t border-white/[0.08] bg-black relative">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-blue-400">QuivixDigital Suite</span>
              <h2 className="text-3xl sm:text-5xl font-bold tracking-tight text-white mt-2 font-heading">
                Built for Speed & Reliability.
              </h2>
            </div>
            <p className="text-sm text-[#86868b] max-w-md">
              Discover the applications and core engines driving user engagement across the QuivixDigital network.
            </p>
          </div>

          {/* App Showcase Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {apps.length > 0 ? (
              apps.map((app, idx) => (
                <Link
                  key={app.id || idx}
                  to={`/apps/${app.id}`}
                  className="group relative apple-glass rounded-3xl p-8 border border-white/[0.08] apple-card-hover flex flex-col justify-between overflow-hidden"
                >
                  {/* Subtle Gradient Glow */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all duration-500" />
                  
                  <div>
                    <div className="w-14 h-14 rounded-2xl bg-neutral-900 border border-white/15 overflow-hidden flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300 shadow-lg">
                      <img src={getAppImage(app)} alt={app.title} className="w-full h-full object-cover" />
                    </div>

                    <span className="text-[10px] font-mono text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">
                      Active Product
                    </span>

                    <h3 className="text-xl font-bold text-white mt-4 group-hover:text-blue-400 transition-colors font-heading">
                      {app.title}
                    </h3>
                    <p className="text-xs text-[#86868b] mt-2 leading-relaxed line-clamp-3">
                      {app.description}
                    </p>
                  </div>

                  <div className="mt-8 pt-4 border-t border-white/[0.06] flex items-center justify-between text-xs text-white/70 group-hover:text-white">
                    <span>Explore Application</span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))
            ) : (
              // Default Fallback High-End Showcase Cards
              [
                { title: "Ziro Platform", desc: "Next-gen intelligent social collector & real-time telemetry engine.", tag: "Consumer Engine" },
                { title: "Chafadi Suite", desc: "Automated enterprise resource coordination and logistics system.", tag: "Enterprise" },
                { title: "Quivix Core AI", desc: "Distributed neural pipeline processing real-time data streams.", tag: "AI Infrastructure" }
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="group relative apple-glass rounded-3xl p-8 border border-white/[0.08] apple-card-hover flex flex-col justify-between"
                >
                  <div>
                    <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 text-white font-bold font-mono">
                      0{idx + 1}
                    </div>
                    <span className="text-[10px] font-mono text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">
                      {item.tag}
                    </span>
                    <h3 className="text-xl font-bold text-white mt-4 font-heading">{item.title}</h3>
                    <p className="text-xs text-[#86868b] mt-2 leading-relaxed">{item.desc}</p>
                  </div>

                  <Link to="/apps" className="mt-8 pt-4 border-t border-white/[0.06] flex items-center justify-between text-xs text-white/70 hover:text-white">
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))
            )}
          </div>

        </div>
      </section>

      {/* ================= STYLISH USER REVIEWS / TESTIMONIALS SECTION ================= */}
      <section className="py-24 border-t border-white/[0.08] bg-[#030305] relative overflow-hidden">
        {/* Background Glowing Mesh & Sparkles */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-gradient-to-r from-blue-600/10 via-indigo-600/10 to-amber-500/10 blur-[130px] rounded-full pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 sm:px-8 relative z-10">
          
          <div className="text-center max-w-2xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-mono mb-3"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
              <span>Verified Ecosystem Reviews</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-5xl font-bold tracking-tight text-white font-heading"
            >
              Trusted by Engineers & Leaders.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-sm text-[#86868b] mt-3 leading-relaxed"
            >
              Real feedback from system architects, product leaders, and enterprise partners using QuivixDigital software.
            </motion.p>
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((rev, idx) => (
              <motion.div
                key={rev.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.12 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative apple-glass rounded-3xl p-6 border border-white/[0.08] apple-card-hover flex flex-col justify-between overflow-hidden shadow-2xl"
              >
                {/* Subtle Top Border Glow */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-400/40 to-transparent group-hover:via-amber-400/60 transition-all duration-500" />
                
                <div>
                  {/* Rating Stars & Top Icon */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1">
                      {[...Array(rev.rating)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <div className="p-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 shadow-md shrink-0">
                      {rev.icon}
                    </div>
                  </div>

                  {/* Review Quote text */}
                  <p className="text-xs text-[#d1d1d6] leading-relaxed relative z-10 italic">
                    "{rev.comment}"
                  </p>
                </div>

                {/* Reviewer Profile Footer */}
                <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-white/20 bg-neutral-900 flex items-center justify-center shrink-0 shadow-md group-hover:border-blue-400 transition-colors">
                    <img
                      src={rev.avatar}
                      alt={rev.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-full items-center justify-center bg-blue-500/10 text-blue-400 font-mono font-bold text-xs">
                      {rev.initials}
                    </div>
                  </div>

                  <div className="overflow-hidden">
                    <h4 className="text-xs font-bold text-white font-heading tracking-tight truncate group-hover:text-blue-400 transition-colors">
                      {rev.name}
                    </h4>
                    <p className="text-[10px] text-[#86868b] truncate">{rev.role}</p>
                    <p className="text-[9px] font-mono text-white/50 truncate">{rev.company}</p>
                  </div>
                </div>

              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* ================= CAREERS & INTERNSHIPS BANNER ================= */}
      <section className="py-16 border-t border-white/[0.08] bg-black">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <div className="apple-glass rounded-2xl p-8 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-6">
            
            <div className="space-y-2 text-left">
              <span className="text-[10px] font-mono uppercase tracking-widest text-sky-400 block">Talent Portal</span>
              <h3 className="text-2xl font-bold text-white font-heading">
                Careers & Internship Program
              </h3>
              <p className="text-xs text-slate-400 max-w-xl leading-relaxed">
                Explore open engineering roles, AI research tracks, academic exams, and placement opportunities at QuivixDigital.
              </p>
            </div>

            <a
              href="https://careers.quivixdigital.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-black font-semibold text-xs hover:bg-slate-200 transition-all shrink-0 active:scale-95"
            >
              <span>Explore Portal</span>
              <ArrowUpRight className="w-4 h-4" />
            </a>

          </div>
        </div>
      </section>

      {/* ================= INVESTOR / CTA BANNER ================= */}
      <section className="py-24 border-t border-white/[0.08] bg-black">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative rounded-3xl bg-gradient-to-b from-neutral-900 to-black p-10 sm:p-16 border border-white/10 overflow-hidden shadow-[0_0_80px_rgba(41,151,255,0.1)]">
            {/* Background Light Beam */}
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
              <div>
                <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest flex items-center justify-center md:justify-start gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" /> High-Growth Ecosystem
                </span>
                <h3 className="text-2xl sm:text-4xl font-bold text-white mt-3 font-heading">
                  Partner with the Future of Digital Infrastructure.
                </h3>
                <p className="text-xs sm:text-sm text-[#86868b] mt-3 max-w-xl">
                  Quivix is rapidly scaling across global markets. Request access to our investor prospectus, financial metrics, and cap table overview.
                </p>
              </div>

              <Link
                to="/invest"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-semibold text-xs hover:bg-neutral-200 transition-all shadow-[0_0_30px_rgba(255,255,255,0.3)] shrink-0 active:scale-95"
              >
                <span>Request Investor Deck</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
